import React from "react";
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  TablePagination,
  Theme,
  Tooltip,
  useTheme,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { ContentCopy } from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate } from "react-router-dom";
import { SortType } from "../../../../../models/SortType";
import {
  formatDate,
  formatDateWithoutTime,
} from "../../../../../views/components/common/functions/formatDate";
import { PurchaseOrderInvoice } from "../../../../../models/Invoice/PurchaseOrderInvoice";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import {
  getModernPageColors,
  modernEmptyStateSx,
} from "../../../../../styles/modernListingPageSx";
import { useNotification } from "../../../../../utils/NotificationsContext";

interface PurchaseOrderInvoicesTableProp {
  maxHeight: number;
  purchaseOrderInvoices: PurchaseOrderInvoice[];
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy?: string;
  sortType?: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortType: SortType) => void;
}

const getApprovalLabel = (invoice: PurchaseOrderInvoice): string => {
  if (invoice.approve_invoice === 1) return "Approved";
  if (invoice.reject_invoice === 1) return "Rejected";
  return "Pending";
};

const getPaymentLabel = (invoice: PurchaseOrderInvoice): string => {
  if (invoice.invoice_payment_status === "paid") return "Paid";
  if (invoice.invoice_payment_status === "partial_paid") return "Partial";
  return "Pending";
};

const formatAmount = (amount?: number): string => {
  if (amount === undefined || amount === null) return "-";
  return `₹${Number(amount).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const PurchaseOrderInvoicesTable = ({
  maxHeight,
  purchaseOrderInvoices,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
}: PurchaseOrderInvoicesTableProp) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const navigate = useNavigate();
  const { showError } = useNotification();

  const sortOptions = [
    { key: "id", label: "Invoice ID" },
    { key: "po_id", label: "PO ID" },
    { key: "invoice_date", label: "Invoice Date" },
    { key: "created_date", label: "Uploaded Date" },
    { key: "expected_payment_date", label: "Payment Date" },
  ];

  const fieldGridSize = { xs: 2, sm: 2, md: 2, lg: 2, xl: 2 };

  const handleChangePage = (_event: unknown, newPage: number) => {
    onPageChange(null, newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    onPageChange(null, 1);
    onRowsPerPageChange(parseInt(event.target.value, 10));
  };

  const handleDownloadLink = (url: string, filename?: string): void => {
    try {
      if (!url || url.trim() === "") return;
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      if (filename) {
        link.download = filename;
      }
      link.click();
    } catch (error) {
      console.error("PurchaseOrderInvoicesTable.handleDownloadLink - Error:", error);
    }
  };

  const handleCopyText = async (
    event: React.MouseEvent,
    value?: string | number | null,
    label = "Value",
  ) => {
    event.stopPropagation();
    const text =
      value === undefined || value === null ? "" : String(value).trim();

    if (!text || text === "-") {
      showError("Copy failed", `Nothing to copy for ${label.toLowerCase()}`);
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error(`Failed to copy ${label}`, error);
    }
  };

  const renderCopyButton = (
    value?: string | number | null,
    label = "Value",
  ) => (
    <Tooltip title={`Copy ${label}`}>
      <IconButton
        size="small"
        onClick={(event) => handleCopyText(event, value, label)}
        sx={{
          p: 0.25,
          color: colors.subtitle,
          "&:hover": {
            backgroundColor: colors.surfaceMuted,
            color: theme.palette.primary.main,
          },
        }}
      >
        <ContentCopy sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  );

  const renderMeta = (label: string, value?: React.ReactNode) => (
    <Box sx={{ minWidth: 0, height: "100%" }}>
      <CustomTypography
        variant="body3"
        color={colors.subtitle}
        fontWeight={600}
        sx={{ display: "block", mb: 0.25 }}
      >
        {label}
      </CustomTypography>
      <Box
        sx={{
          minHeight: 24,
          display: "flex",
          alignItems: "center",
        }}
      >
        {typeof value === "string" || typeof value === "number" || !value ? (
          <CustomTypography
            variant="body2"
            color={colors.title}
            fontWeight={500}
            sx={{
              fontSize: "0.78rem",
              lineHeight: 1.35,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {value || "-"}
          </CustomTypography>
        ) : (
          value
        )}
      </Box>
    </Box>
  );

  const openInvoiceDetails = (invoice: PurchaseOrderInvoice) => {
    try {
      if (!invoice.pr_id) return;
      navigate(
        `/purchase/requests/details?prId=${invoice.pr_id}&projectId=${invoice.order_id}`,
      );
    } catch (error) {
      console.error("PurchaseOrderInvoicesTable.openInvoiceDetails - Error:", error);
    }
  };

  return (
    <Paper
      sx={{
        marginTop: "0.6rem",
        borderRadius: "16px",
        border: `1px solid ${colors.border}`,
        boxShadow: colors.shadowSoft,
        overflow: "hidden",
        backgroundColor: colors.surface,
      }}
    >
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1.5, sm: 2 },
          borderBottom: `1px solid ${colors.border}`,
          backgroundColor: colors.surfaceMuted,
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Box>
            <CustomTypography variant="heading7" fontWeight={700}>
              Vendor Invoices
            </CustomTypography>
            <CustomTypography variant="caption" color={colors.subtitle}>
              {total_count} total invoices
            </CustomTypography>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
          >
            <CustomTypography variant="caption" color={colors.subtitle}>
              Sort by
            </CustomTypography>
            {sortOptions.map((option) => {
              const isActive = sortBy === option.key;
              const directionLabel =
                isActive && sortType === SortType.ASC ? "Asc" : "Desc";

              return (
                <AppButton
                  key={option.key}
                  label={
                    isActive ? `${option.label}: ${directionLabel}` : option.label
                  }
                  onClick={() =>
                    onSortChange(
                      option.key,
                      isActive && sortType === SortType.DESC
                        ? SortType.ASC
                        : SortType.DESC,
                    )
                  }
                  customStyles={{
                    minWidth: "fit-content",
                    height: 34,
                    px: 1.5,
                    borderRadius: "999px",
                    color: isActive ? colors.surface : colors.title,
                    backgroundColor: isActive
                      ? theme.palette.primary.main
                      : colors.surface,
                    border: `1px solid ${
                      isActive ? theme.palette.primary.main : colors.border
                    }`,
                    "&:hover": {
                      backgroundColor: isActive
                        ? theme.palette.primary.dark
                        : colors.surfaceMuted,
                    },
                  }}
                />
              );
            })}
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={{
          maxHeight: maxHeight || window.innerHeight - 340,
          overflowY: "auto",
          p: { xs: 1, sm: 1.5 },
          backgroundColor: colors.pageBg,
        }}
      >
        <Stack spacing={1}>
          {purchaseOrderInvoices.map((invoice) => {
            const approvalLabel = getApprovalLabel(invoice);
            const paymentLabel = getPaymentLabel(invoice);

            return (
              <Paper
                key={invoice.id}
                onClick={() => openInvoiceDetails(invoice)}
                sx={{
                  cursor: invoice.pr_id ? "pointer" : "default",
                  borderRadius: "10px",
                  overflow: "hidden",
                  backgroundColor: colors.surface,
                  border: `1px solid ${colors.border}`,
                  boxShadow: "none",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <Box sx={{ px: { xs: 1, sm: 1.25 }, pt: 1, pb: 0.75 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={1}
                  >
                    <Stack
                      direction="row"
                      spacing={0.25}
                      alignItems="center"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <CustomTypography
                        variant="heading7"
                        fontWeight={700}
                        color={colors.title}
                        sx={{ fontSize: "0.9rem", lineHeight: 1.2 }}
                      >
                        #{invoice.id || "-"}
                      </CustomTypography>
                      {renderCopyButton(invoice.id, "Invoice ID")}
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {invoice.invoice_url ? (
                        <Tooltip title="Download Invoice">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDownloadLink(
                                invoice.invoice_url,
                                `Invoice_${invoice.invoice_number || invoice.id}.pdf`,
                              )
                            }
                            sx={{
                              color: theme.palette.primary.main,
                              p: 0.4,
                            }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Stack>
                  </Stack>

                  <Grid2
                    container
                    spacing={1.5}
                    columns={{ xs: 4, sm: 6, md: 8, lg: 10, xl: 10 }}
                    sx={{ mt: 1, mb: 0.5, alignItems: "stretch" }}
                  >
                    <Grid2 size={fieldGridSize}>
                      {renderMeta("PO ID", `#${invoice.po_id || "-"}`)}
                    </Grid2>
                    <Grid2 size={fieldGridSize}>
                      {renderMeta("Vendor", invoice.vendor_name || "-")}
                    </Grid2>
                    <Grid2 size={fieldGridSize}>
                      {renderMeta(
                        "Invoice Number",
                        invoice.invoice_number || "-",
                      )}
                    </Grid2>
                    <Grid2 size={fieldGridSize}>
                      {renderMeta(
                        "Approval",
                        <Chip
                          label={approvalLabel.toUpperCase()}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            backgroundColor:
                              approvalLabel === "Approved"
                                ? `${theme.palette.success.main}15`
                                : approvalLabel === "Rejected"
                                  ? `${theme.palette.error.main}15`
                                  : `${theme.palette.warning.main}15`,
                            color:
                              approvalLabel === "Approved"
                                ? theme.palette.success.main
                                : approvalLabel === "Rejected"
                                  ? theme.palette.error.main
                                  : theme.palette.warning.main,
                          }}
                        />,
                      )}
                    </Grid2>
                    <Grid2 size={fieldGridSize}>
                      {renderMeta(
                        "Payment Status",
                        <Chip
                          label={paymentLabel.toUpperCase()}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            backgroundColor: colors.surfaceMuted,
                            border: `1px solid ${colors.border}`,
                            color: colors.title,
                          }}
                        />,
                      )}
                    </Grid2>
                    <Grid2 size={fieldGridSize}>
                      {renderMeta(
                        "Invoice Amount",
                        formatAmount(invoice.invoice_amount),
                      )}
                    </Grid2>
                    <Grid2 size={fieldGridSize}>
                      {renderMeta(
                        "Invoice Date",
                        formatDate(invoice.invoice_date),
                      )}
                    </Grid2>
                    <Grid2 size={fieldGridSize}>
                      {renderMeta(
                        "Payment Term",
                        invoice.payment_term != null
                          ? `${invoice.payment_term} days`
                          : "-",
                      )}
                    </Grid2>
                    <Grid2 size={fieldGridSize}>
                      {renderMeta(
                        "Expected Payment Date",
                        formatDateWithoutTime(invoice.expected_payment_date) ||
                          "-",
                      )}
                    </Grid2>
                  </Grid2>
                </Box>

                <Box
                  sx={{
                    px: { xs: 1, sm: 1.25 },
                    py: 0.6,
                    borderTop: `1px solid ${colors.borderSoft}`,
                    backgroundColor: colors.surface,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <CustomTypography
                    variant="body3"
                    color={colors.subtitle}
                    fontWeight={600}
                  >
                    Uploaded on {formatDate(invoice.created_date)}
                    {invoice.upload_invoice_admin_name
                      ? ` by ${invoice.upload_invoice_admin_name}`
                      : ""}{" "}
                    | Updated on {formatDate(invoice.modified_date)}
                  </CustomTypography>
                  {invoice.pr_id ? (
                    <AppButton
                      label="View"
                      onClick={() => openInvoiceDetails(invoice)}
                      customStyles={{
                        minWidth: "fit-content",
                        minHeight: 30,
                        px: 1.25,
                        fontSize: "0.72rem",
                      }}
                    />
                  ) : null}
                </Box>
              </Paper>
            );
          })}
        </Stack>
      </Box>

      {purchaseOrderInvoices.length === 0 && (
        <Box
          sx={{
            ...modernEmptyStateSx(theme),
            p: 4,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: colors.surface,
          }}
        >
          <CustomTypography
            variant="heading6"
            fontWeight={600}
            color={colors.subtitle}
          >
            No purchase order invoices match the current search criteria.
          </CustomTypography>
        </Box>
      )}

      {total_count > 0 && (
        <TablePagination
          rowsPerPageOptions={[30, 50, 100, 200]}
          component="div"
          count={total_count}
          rowsPerPage={limit}
          page={page_no - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: colors.surface,
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: "0.8rem",
                color: colors.subtitle,
              },
          }}
        />
      )}
    </Paper>
  );
};

export default PurchaseOrderInvoicesTable;
